import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { DropdownButton, MenuItem, Modal } from 'react-bootstrap';
import Toggle from 'react-toggle';

import { BannerPlacement, BannerScope, BannerStyle } from '../../../../../typings/enums';
import { window } from '../../../../globals';
import { withTargetChecked } from '../../../../utils';
import Button from '../../../generic/Button/Button';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import CharacterCount from '../../../generic/Forms/CharacterCount';
import FormGroup from '../../../generic/Forms/FormGroup';
import FormLabel from '../../../generic/Forms/FormLabel';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import { icons } from '../../../generic/Icon/icons';
import QuillEditor from '../../../generic/QuillEditor';
import type { CreateBannerRequest } from '../../../../../shared/types/routes/banners';

import { BannerOrganizationsPicker } from './BannerOrganizationsPicker';
import type { State as AddEditState, UseAddEditBanner } from './useAddEditBanner';

import styles from './ModalAddEditBanner.module.less';

const bannerDuration = (startDate: string, endDate: string | null) => {
  if (!endDate) {
    return 'Ongoing';
  }

  const start = moment(startDate);
  const end = moment(endDate);
  if (start.isAfter(end)) {
    // Invalid inputs.
    return <span className="italic">Invalid</span>;
  }

  // A second is added to the end date to ensure that a day is counted from the start to the end of a single day.
  // The "humanize" format of moment.js is not precise enough, and simply appending "# months" to "# days" when the
  // duration is more than a month doesn't work either because of the way moment.js converts a duration to days.
  return moment.duration(end.add(1, 'second').diff(start)).format();
};

const dateInputFormat = 'YYYY-MM-DD';

// Display label for every BannerStyle. The management UI only exposes Low/Medium/High for
// authoring (see `authorableBannerStyles`), but Success/Info exist on the enum and may be
// persisted on banners created elsewhere; keep explicit labels so the dropdown reflects
// the real persisted style instead of falling back to the default.
const bannerStyleToLabelMap: Record<BannerStyle, string> = {
  [BannerStyle.Low]: 'Blue (default)',
  [BannerStyle.Medium]: 'Yellow',
  [BannerStyle.High]: 'Red',
  [BannerStyle.Success]: 'Unsupported (Success)',
  [BannerStyle.Info]: 'Unsupported (Info)',
};

const authorableBannerStyles: BannerStyle[] = [
  BannerStyle.Low,
  BannerStyle.Medium,
  BannerStyle.High,
];

const placementLabelMap: Record<BannerPlacement, string> = {
  [BannerPlacement.All]: 'All',
  [BannerPlacement.Login]: 'Login',
  [BannerPlacement.Dashboard]: 'Dashboard',
};

const scopeLabelMap: Record<BannerScope, string> = {
  [BannerScope.Global]: 'Global',
  [BannerScope.Organizations]: 'Org-Scoped',
};

// bannerDataIsValid makes some basic checks that the inputs to create or update a banner are valid.
const bannerDataIsValid = (banner: Omit<CreateBannerRequest, 'body'>): boolean => {
  if (!banner.title) {
    return false;
  }

  if (banner.endDate && !moment(banner.endDate).isAfter(moment(banner.startDate))) {
    return false;
  }

  // Org-scoped banners must have at least one selected organization.
  if (banner.scope?.type === BannerScope.Organizations && banner.scope.organizationIds.length === 0) {
    return false;
  }

  return true;
};

type ModalAddEditBannerProps = Omit<
  UseAddEditBanner,
  'state' | 'onAddNewBanner' | 'onEditBanner' | 'onDuplicateBanner'
> & {
  state: AddEditState;
  isCreatingOrUpdating: boolean;
  onSubmitAddEdit: () => void;
};

/**
 * Banner for creating a new banner or editing an existing one.
 */
const ModalAddEditBanner = ({
  state,
  isEdited,
  isCreatingOrUpdating,
  onCloseModal,
  onTitleChange,
  onBodyChange,
  onStartDateChange,
  onEndDateChange,
  onToggleIsDismissible,
  onBannerStyleChange,
  onPlacementChange,
  onScopeChange,
  onScopedOrganizationsChange,
  onSubmitAddEdit,
}: ModalAddEditBannerProps) => {
  const isSuperUser = !!window.user?.super;
  const currentScope: BannerScope = state.banner.scope?.type ?? BannerScope.Global;
  const currentPlacement: BannerPlacement = state.banner.placement ?? BannerPlacement.All;
  const isOrgScoped = currentScope === BannerScope.Organizations;
  // Org-scoping is only valid alongside dashboard placement (login is anonymous).
  const isScopeLocked = currentPlacement !== BannerPlacement.Dashboard;
  const selectedOrgIds = state.banner.scope?.type === BannerScope.Organizations
    ? state.banner.scope.organizationIds
    : [];
  // Edit and duplicate flows ship with persisted scopedOrganizations metadata so we can
  // render chips with real titles before the user runs any search.
  const persistedScopedOrgs = state.scopedOrganizations;

  return (
    <Modal
      onHide={onCloseModal}
      show
      className={styles.modal}
      bsSize="large"
    >
      <form onSubmit={(event) => {
        event.preventDefault();
        onSubmitAddEdit();
      }}>
        <header className={styles.header}>
          <div className={styles.heading}>
            {state.mode === 'edit' ? 'Edit Notification banner' : 'Add Notification banner'}
          </div>
          <div className={styles.title}>
            {state.mode === 'edit'
              ? state.originalTitle
              : (state.banner.title || <span className={styles.untitled}>(untitled)</span>)}
          </div>
        </header>
        <Modal.Body>
          <hr />
          <FormGroup
            legend="Content"
            alignLegend="left"
            direction="column"
            spacing="sm"
          >
            <LabeledTextInput
              labelVariant="legacy"
              label="Title"
              placeholder="A title for your banner"
              onChange={onTitleChange}
              value={state.banner.title}
              maxLength={50}
              showCharCount
              isRequired
            />
            <div>
              {/* We are not wrapping the QuillEditor in the label, as it forces all text to be bold. */}
              <FormLabel variant="legacy" label="Body" requiredText="(Required)" />
              <QuillEditor
                totalHeight={180}
                initialContents={state.banner.body.contents}
                quillOptions={{
                  modules: {
                    toolbar: ['bold', 'italic', 'link'],
                  },
                  placeholder: 'Some text for your banner',
                }}
                maxLength={250}
                onChange={onBodyChange}
              />
              <CharacterCount
                text="Characters: "
                maxLength={250}
                currentLength={state.banner.body.length}
              />
            </div>
          </FormGroup>
          <hr />
          <FormGroup
            legend="Schedule"
            alignLegend="left"
            direction="row"
            spacing="sm"
          >
            <FormLabel
              variant="legacy"
              label="Start Date"
            >
              <input
                type="date"
                className={styles.dateInput}
                value={moment(state.banner.startDate).format(dateInputFormat)}
                onChange={event => onStartDateChange(moment(event.target.value).startOf('day').toISOString())}
                min={state.mode === 'add' ? moment().format(dateInputFormat) : undefined}
              />
              <div className={styles.dateInputRequired}>Required</div>
            </FormLabel>
            <FormLabel
              variant="legacy"
              label="End Date"
            >
              <input
                type="date"
                className={styles.dateInput}
                value={state.banner.endDate ? moment(state.banner.endDate).format(dateInputFormat) : ''}
                onChange={event => onEndDateChange(moment(event.target.value).endOf('day').toISOString())}
                min={state.mode === 'add' ? moment().format(dateInputFormat) : undefined}
              />
              {state.banner.endDate && (
                <button
                  type="button"
                  className={styles.dateInputClear}
                  onClick={() => {
                    onEndDateChange(null);
                  }}
                >
                  Clear
                </button>
              )}
            </FormLabel>
            <div className={styles.duration}>
              Duration:<br />{bannerDuration(state.banner.startDate, state.banner.endDate)}
            </div>
          </FormGroup>
          <hr />
          <FormGroup
            legend="Configuration"
            alignLegend="left"
            direction="row"
            spacing="sm"
          >
            <FormLabel
              variant="legacy"
              label="Style"
            >
              <DropdownButton
                id="banner-style-dropdown"
                className={styles.dropdown}
                title={<span>{bannerStyleToLabelMap[state.banner.style]}</span>}
                onSelect={onBannerStyleChange as (eventKey: unknown) => void}
              >
                {authorableBannerStyles.map((style) => (
                  <MenuItem
                    key={style}
                    active={state.banner.style === style}
                    eventKey={style}
                  >
                    <span>{bannerStyleToLabelMap[style]}</span>
                  </MenuItem>
                ))}
              </DropdownButton>
            </FormLabel>
            <FormLabel
              variant="legacy"
              label="Placement"
            >
              <DropdownButton
                id="banner-placement-dropdown"
                className={styles.dropdown}
                title={<span>{placementLabelMap[currentPlacement]}</span>}
                onSelect={(eventKey) => onPlacementChange(eventKey as BannerPlacement)}
              >
                {Object.values(BannerPlacement).map((placement) => (
                  <MenuItem
                    key={placement}
                    active={currentPlacement === placement}
                    eventKey={placement}
                  >
                    {placementLabelMap[placement]}
                  </MenuItem>
                ))}
              </DropdownButton>
            </FormLabel>
            <FormLabel
              variant="legacy"
              label="Dismissible"
            >
              <Toggle
                id="banner-dismissible-toggle"
                checked={state.banner.isDismissible}
                onChange={withTargetChecked(onToggleIsDismissible)}
                icons={false}
              />
            </FormLabel>
          </FormGroup>
          {isSuperUser && (
            <FormGroup
              legend="Visibility"
              alignLegend="left"
              direction="column"
              spacing="sm"
            >
              <FormLabel
                variant="legacy"
                label="Scope"
              >
                <DropdownButton
                  id="banner-scope-dropdown"
                  className={styles.dropdown}
                  title={<span>{scopeLabelMap[currentScope]}</span>}
                  onSelect={(eventKey) => onScopeChange(eventKey as BannerScope)}
                  disabled={isScopeLocked}
                >
                  {Object.values(BannerScope).map((scope) => (
                    <MenuItem
                      key={scope}
                      active={currentScope === scope}
                      eventKey={scope}
                    >
                      {scopeLabelMap[scope]}
                    </MenuItem>
                  ))}
                </DropdownButton>
                {isScopeLocked && (
                  <div className={styles.helperText}>
                    Org-scoped banners are only available on the dashboard placement, as login-page requests are
                    anonymous.
                  </div>
                )}
              </FormLabel>
              {isOrgScoped && (
                <FormLabel
                  variant="legacy"
                  label="Target Organizations"
                  requiredText="(Required)"
                >
                  <BannerOrganizationsPicker
                    selectedIds={selectedOrgIds}
                    initialOrganizations={persistedScopedOrgs}
                    isEnabled={isSuperUser && isOrgScoped}
                    onChange={onScopedOrganizationsChange}
                  />
                </FormLabel>
              )}
            </FormGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button
              type="secondary"
              size="medium"
              onClick={onCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="medium"
              onClick={_.noop /* Submission is handled at the form level. */}
              isDisabled={
                // If the form hasn't been edited but the banner is deactivated, allow submission to activate it.
                (!isEdited && (state.mode !== 'edit' || !state.banner.isDeactivated)) ||
                isCreatingOrUpdating ||
                !bannerDataIsValid(state.banner)
              }
              icon={isCreatingOrUpdating ? icons.circleO : undefined}
              isSubmitButton
            >
              Submit{state.mode === 'edit' && state.banner.isDeactivated && ' (activate)'}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ModalAddEditBanner;
